import React, { useState, useEffect, useMemo } from 'react';
import { DatePicker, Spin, message, Typography, Row, Col, Card, Radio, Select } from 'antd';
import { XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import StitchLayout from '../../components/Crm/CrmLayout';
import { FireOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;
const { Title } = Typography;

interface StatusCodeCount {
    status_code: number;
    count: number;
}

interface DataPoint {
    count?: number;
    total_count?: number;
    timestamp: number;
    status_codes?: StatusCodeCount[];
}

const DeveloperToolkitTrace: React.FC = () => {
    const [quickFilter, setQuickFilter] = useState<string>('today');
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
        dayjs().startOf('day'),
        dayjs().endOf('day')
    ]);
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<DataPoint[]>([]);
    const [bucketDuration, setBucketDuration] = useState<number | 'auto'>('auto');

    const fetchAnalytics = async (start: Dayjs, end: Dayjs, currentBucketSetting: number | 'auto') => {
        setLoading(true);
        try {
            let finalBucketDuration = 60; // default 1 hour in minutes

            if (currentBucketSetting === 'auto') {
                const diffInSeconds = end.diff(start, 'second');
                if (diffInSeconds <= 3600) {
                    finalBucketDuration = 1; // 1 min
                } else if (diffInSeconds <= 86400) {
                    finalBucketDuration = 60; // 1 hour
                } else if (diffInSeconds <= 3 * 86400) {
                    finalBucketDuration = 240; // 4 hours
                } else if (diffInSeconds <= 7 * 86400) {
                    finalBucketDuration = 360; // 6 hours
                } else {
                    finalBucketDuration = 1440; // 1 day
                }
            } else {
                finalBucketDuration = currentBucketSetting;
            }

            const response = await axiosInstance.get(apis.FRESHDESK_API_CALLS_ANALYTICS, {
                params: {
                    start_date: start.toISOString(),
                    end_date: end.toISOString(),
                    bucket_duration_minutes: finalBucketDuration
                }
            });

            if (response?.data?.status && response?.data?.data?.analytics) {
                // Ensure data is sorted chronologically for the area chart
                const sortedData = [...response.data.data.analytics].sort((a: any, b: any) => a.timestamp - b.timestamp);
                setData(sortedData);
            } else {
                setData([]);
                message.error('Failed to load data.');
            }
        } catch (error) {
            console.error('Error fetching analytics:', error);
            message.error('Error fetching analytics data');
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (dateRange[0] && dateRange[1]) {
            fetchAnalytics(dateRange[0], dateRange[1], bucketDuration);
        }
    }, [dateRange, bucketDuration]);

    const handleDateChange = (dates: any) => {
        setQuickFilter('');
        if (dates && dates.length === 2) {
            // Cap at 90 days maximum
            const diffDays = dates[1].diff(dates[0], 'day');
            if (diffDays > 90) {
                message.warning('Date range cannot exceed 90 days. Truncating to 90 days.');
                setDateRange([dates[0].startOf('day'), dates[0].add(90, 'day').endOf('day')]);
            } else {
                setDateRange([dates[0].startOf('day'), dates[1].endOf('day')]);
            }
        }
    };

    const handleQuickFilter = (e: any) => {
        const filter = e.target.value;
        setQuickFilter(filter);
        const end = dayjs().endOf('day');
        let start = dayjs().startOf('day');

        if (filter === 'today') {
            start = dayjs().startOf('day');
        } else if (filter === 'yesterday') {
            start = dayjs().subtract(1, 'day').startOf('day');
            setDateRange([start, dayjs().subtract(1, 'day').endOf('day')]);
            return;
        } else if (filter === '3days') {
            start = dayjs().subtract(2, 'day').startOf('day');
        } else if (filter === '7days') {
            start = dayjs().subtract(6, 'day').startOf('day');
        } else if (filter === '30days') {
            start = dayjs().subtract(29, 'day').startOf('day');
        } else if (filter === '90days') {
            start = dayjs().subtract(89, 'day').startOf('day');
        }

        setDateRange([start, end]);
    };

    const formattedData = useMemo(() => {
        return data.map(item => {
            const date = moment(item.timestamp).utcOffset('+05:30');
            const diffInDays = dateRange[1].diff(dateRange[0], 'day');

            // Format intelligently based on selected range
            let formatStr = 'MMM DD, HH:mm';
            if (diffInDays <= 1) {
                formatStr = 'HH:mm';
            } else if (diffInDays > 7) {
                formatStr = 'MMM DD';
            }

            const formattedItem: any = {
                ...item,
                timeLabel: date.format(formatStr),
                fullDate: date.format('YYYY-MM-DD HH:mm:ss')
            };

            if (item.status_codes) {
                item.status_codes.forEach(sc => {
                    formattedItem[`status_${sc.status_code}`] = sc.count;
                });
            }

            return formattedItem;
        });
    }, [data, dateRange]);

    const totalCalls = useMemo(() => {
        return data.reduce((acc, curr) => acc + (curr.total_count || curr.count || 0), 0);
    }, [data]);

    const uniqueStatusCodes = useMemo(() => {
        const codes = new Set<number>();
        data.forEach(item => {
            item.status_codes?.forEach(sc => codes.add(sc.status_code));
        });
        return Array.from(codes).sort();
    }, [data]);

    const getStatusColor = (code: number) => {
        if (code >= 200 && code < 300) return '#10b981'; // Green
        if (code >= 300 && code < 400) return '#3b82f6'; // Blue
        if (code === 429) return '#f59e0b'; // Amber for rate limit
        if (code >= 400 && code < 500) return '#f97316'; // Orange
        if (code >= 500) return '#ef4444'; // Red
        return '#6b7280'; // Gray
    };

    return (
        <StitchLayout>
            <div style={{ padding: '0px', fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6 flex flex-wrap gap-4 items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                                <FireOutlined />
                            </div>
                            <Title level={4} className="!m-0">Developer Toolkit Trace</Title>
                        </div>
                        <p className="text-gray-500 text-sm mt-1">Track the volume of Freshdesk API calls. Max filter 90 days.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Select
                            value={bucketDuration}
                            onChange={setBucketDuration}
                            style={{ width: 140 }}
                            options={[
                                { value: 'auto', label: 'Auto Bucket' },
                                { value: 1, label: '1 Minute' },
                                { value: 5, label: '5 Minutes' },
                                { value: 15, label: '15 Minutes' },
                                { value: 30, label: '30 Minutes' },
                                { value: 60, label: '1 Hour' },
                                { value: 240, label: '4 Hours' },
                                { value: 360, label: '6 Hours' },
                                { value: 1440, label: '1 Day' },
                            ]}
                        />
                        <Radio.Group value={quickFilter} onChange={handleQuickFilter} optionType="button" buttonStyle="solid">
                            <Radio.Button value="today">Today</Radio.Button>
                            <Radio.Button value="yesterday">Yesterday</Radio.Button>
                            <Radio.Button value="3days">Last 3 Days</Radio.Button>
                            <Radio.Button value="7days">7 Days</Radio.Button>
                            <Radio.Button value="30days">30 Days</Radio.Button>
                            <Radio.Button value="90days">90 Days</Radio.Button>
                        </Radio.Group>
                        <RangePicker
                            value={dateRange as any}
                            onChange={handleDateChange}
                            disabledDate={(current) => current && current > dayjs().endOf('day')}
                            className="w-[350px]"
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center h-64 bg-white rounded-lg shadow-sm">
                        <Spin size="large" tip="Loading Analytics..." />
                    </div>
                ) : (
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Card className="shadow-sm">
                                <div className="mb-4">
                                    <p className="text-gray-500 text-sm font-medium">Total API Calls (Selected Range)</p>
                                    <Title level={2} className="!m-0 !text-indigo-600">{totalCalls.toLocaleString()}</Title>
                                </div>
                                <div style={{ width: '100%', height: 400 }}>
                                    <ResponsiveContainer>
                                        <AreaChart
                                            data={formattedData}
                                            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                        >
                                            <defs>
                                                <linearGradient id="grad-apicalls" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.6} />
                                                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0.04} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                            <XAxis
                                                dataKey="timeLabel"
                                                stroke="#6B7280"
                                                tick={{ fill: '#6B7280', fontSize: 12 }}
                                                tickLine={false}
                                                axisLine={{ stroke: '#E5E7EB' }}
                                                minTickGap={30}
                                            />
                                            <YAxis
                                                stroke="#6B7280"
                                                tick={{ fill: '#6B7280', fontSize: 12 }}
                                                tickLine={false}
                                                axisLine={false}
                                            />
                                            <RechartsTooltip
                                                content={({ active, payload }: any) => {
                                                    if (active && payload && payload.length) {
                                                        const fullDate = payload[0]?.payload?.fullDate;
                                                        const total = payload[0]?.payload?.total_count || payload[0]?.payload?.count || 0;
                                                        return (
                                                            <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md min-w-[150px]">
                                                                <p className="font-bold text-gray-700 mb-2">{fullDate}</p>
                                                                <div className="space-y-1">
                                                                    {payload.map((entry: any, index: number) => (
                                                                        <div key={index} className="flex items-center justify-between gap-4">
                                                                            <span style={{ color: entry.color }} className="font-medium text-sm">
                                                                                {entry.name}
                                                                            </span>
                                                                            <span className="font-bold text-gray-900 text-sm">
                                                                                {entry.value}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                    <div className="pt-2 mt-2 border-t border-gray-100 flex items-center justify-between gap-4">
                                                                        <span className="font-medium text-gray-600 text-sm">Total Calls</span>
                                                                        <span className="font-bold text-gray-900 text-sm">{total}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                }}
                                            />
                                            {uniqueStatusCodes.length > 0 ? (
                                                uniqueStatusCodes.map(code => (
                                                    <Area
                                                        key={code}
                                                        type="monotone"
                                                        dataKey={`status_${code}`}
                                                        name={`Status ${code}`}
                                                        stackId="1"
                                                        stroke={getStatusColor(code)}
                                                        fill={getStatusColor(code)}
                                                        fillOpacity={0.6}
                                                        activeDot={{ r: 6, fill: getStatusColor(code), stroke: '#fff', strokeWidth: 2 }}
                                                        animationDuration={1000}
                                                    />
                                                ))
                                            ) : (
                                                <Area
                                                    type="monotone"
                                                    dataKey="count"
                                                    name="Total Calls"
                                                    stroke="#6366f1"
                                                    strokeWidth={3}
                                                    fill="url(#grad-apicalls)"
                                                    activeDot={{ r: 6, fill: '#6366f1', stroke: '#fff', strokeWidth: 2 }}
                                                    animationDuration={1000}
                                                />
                                            )}
                                        </AreaChart>
                                    </ResponsiveContainer>
                                    {formattedData.length === 0 && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                                            <p className="text-gray-500">No data available for the selected range</p>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                )}
            </div>
        </StitchLayout>
    );
};

export default DeveloperToolkitTrace;
